/**
 * สรุปมาสาย — owner/HR/manager ดูสรุปเวลาพนักงานที่มาสาย (หน่วยนาที/ชั่วโมง)
 * ย้อนหลัง ค่าเริ่มต้นเป็นรอบเงินเดือนปัจจุบัน เลือกรอบย้อนหลังหรือกรอกวันที่เองได้.
 * คำนวณสดจาก attendance_events ทุกครั้ง (ไม่อ่าน monthly_summary.late_minutes ซึ่งค้าง
 * จนกว่าจะกด "สรุปเวลา" ของงวดนั้น).
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, Download, FileSpreadsheet, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLateReport, type LateReportRow } from '../../hooks';
import { lateLabel } from '../../lib/late-format';
import { downloadLateSummaryXlsx } from '../../lib/late-summary-xlsx';
import {
  bkkToday, cycleOfRange, cycleOption, dailyCsvPath, dailyPeriodOf, rangeFromParams, recentCycles, sameRange, summaryCsvPath, type DateRange,
} from '../../lib/pay-cycle-range';
import { last7DaysRange, last30DaysRange } from '../../components/domain/AttendanceTimeline';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState';
import { useAuthStore } from '../../lib/store';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

/** รอบที่เลือกย้อนหลังได้ใน dropdown (API รับช่วงเดียวได้ไม่เกิน 366 วันอยู่แล้ว) */
const CYCLE_CHOICES = 24;

function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', timeZone: 'Asia/Bangkok' }).format(
    new Date(`${iso}T00:00:00+07:00`),
  );
}

async function downloadCsv(path: string, filename: string, onError: (msg: string) => void) {
  const jwt = useAuthStore.getState().jwt;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    onError(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
  }
}

/** ขาด/ลา: null = คนที่ไม่ร่วมสรุปเวลา (ระบบไม่นับให้) · ลาครึ่งวันแสดง 0.5 */
function dayCount(n: number | null | undefined): string {
  if (n == null) return '—';
  return String(Math.round(n * 100) / 100);
}

function SkeletonRows({ colSpan }: { colSpan: number }) {
  return (
    <>
      {[1, 2, 3, 4].map((i) => (
        <tr key={i} className="border-b border-gray-50 last:border-0">
          <td className="px-3 py-2" colSpan={colSpan}>
            <div className="h-4 bg-gray-100 rounded w-full animate-pulse" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function LateReport() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qFrom = searchParams.get('from');
  const qTo = searchParams.get('to');
  const today = bkkToday();
  const cycles = useMemo(() => recentCycles(today, CYCLE_CHOICES), [today]);
  const currentCycle = cycles[0]!;
  const previousCycle = cycles[1]!;
  const [range, setRangeState] = useState<DateRange>(
    rangeFromParams(qFrom, qTo) ?? { from: currentCycle.from, to: currentCycle.to },
  );
  const [showAll, setShowAll] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const companyName = useAuthStore((s) => s.profile?.tenant_name ?? '');

  function applyRange(next: DateRange) {
    setRangeState(next);
    setSearchParams({ from: next.from, to: next.to }, { replace: true });
  }

  function onDateInput(field: 'from' | 'to', value: string) {
    if (!value) return;
    applyRange({ ...range, [field]: value });
  }

  const { data, isLoading, isError, refetch } = useLateReport(range);
  const rows: LateReportRow[] = (data?.rows ?? []).filter((r) => showAll || r.late_days > 0);
  const leaveColumns = data?.leave_columns ?? [];

  const presets: Array<{ id: string; label: string; range: DateRange }> = [
    { id: 'this-cycle', label: `รอบนี้ ${currentCycle.label}`, range: currentCycle },
    { id: 'last-cycle', label: `รอบก่อน ${previousCycle.label}`, range: previousCycle },
    { id: 'last-7', label: '7 วัน', range: last7DaysRange() },
    { id: 'last-30', label: '30 วัน', range: last30DaysRange() },
  ];
  const selectedCycle = cycleOfRange(range);
  const dailyCycle = cycleOption(dailyPeriodOf(range));

  // ไฟล์รายแผนกใช้ข้อมูลชุดเดียวกับตาราง (ทุกคน ไม่ขึ้นกับช่อง "แสดงคนไม่สายด้วย")
  async function exportSummary() {
    const all = data?.rows ?? [];
    if (!data || all.length === 0) {
      setDownloadError('ไม่มีพนักงานในช่วงวันที่นี้');
      return;
    }
    setDownloadError(null);
    setExporting(true);
    try {
      await downloadLateSummaryXlsx({ company: companyName, from: data.from, to: data.to, leaveColumns, rows: all });
    } catch (e) {
      setDownloadError(`สร้างไฟล์ไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <AdminLayout
      title="สรุปมาสาย"
      wide
      actions={
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className="btn-secondary btn-slim inline-flex items-center gap-1.5"
            onClick={() =>
              void downloadCsv(
                summaryCsvPath(range, showAll),
                `สรุปมาสาย-${range.from}_${range.to}.csv`,
                setDownloadError,
              )
            }
          >
            <Download className="w-4 h-4" /> สรุป (CSV)
          </button>
          <button
            type="button"
            className="btn-cta btn-slim inline-flex items-center gap-1.5"
            title={`ดึงรายวันทั้งงวด ${dailyCycle.label}: วันไหน/ใคร/สายกี่นาที/หักเท่าไหร่ (ตรงกับสลิป) อ่านจากผล "สรุปเวลาเข้างาน" ของงวดนั้น เปิดใน Excel ได้`}
            onClick={() =>
              void downloadCsv(
                dailyCsvPath(range),
                `หักมาสายรายวัน-${dailyCycle.period}.csv`,
                setDownloadError,
              )
            }
          >
            <Download className="w-4 h-4" /> หักมาสายรายวัน (Excel)
          </button>
          <button
            type="button"
            className="btn-secondary btn-slim inline-flex items-center gap-1.5"
            title="1 ชีตต่อแผนก: รหัส · ชื่อ · สาย · ขาด · ลาแต่ละประเภท · ลาฉุกเฉิน ตามช่วงวันที่ที่เลือก ทุกคนในแผนก"
            disabled={isLoading || isError || exporting}
            onClick={() => void exportSummary()}
          >
            <FileSpreadsheet className="w-4 h-4" /> {exporting ? 'กำลังสร้างไฟล์…' : 'สรุปรายงาน (Excel)'}
          </button>
        </div>
      }
    >
      {downloadError && <p className="text-xs text-red-600 mb-2">{downloadError}</p>}

      <div className="card p-3 mb-3">
        <div className="flex gap-1.5 mb-3 overflow-x-auto">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyRange({ from: p.range.from, to: p.range.to })}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
                sameRange(p.range, range) ? 'bg-brand-100 text-brand-700 font-medium' : 'bg-white border border-gray-200 text-gray-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mb-3">
          <label className="label">รอบเงินเดือน</label>
          <select
            value={selectedCycle?.period ?? ''}
            onChange={(e) => {
              const picked = cycles.find((c) => c.period === e.target.value);
              if (picked) applyRange({ from: picked.from, to: picked.to });
            }}
            className="input w-full"
          >
            {!cycles.some((c) => c.period === selectedCycle?.period) && (
              <option value={selectedCycle?.period ?? ''}>{selectedCycle?.label ?? 'ช่วงกำหนดเอง (ไม่ตรงรอบ)'}</option>
            )}
            {cycles.map((c) => (
              <option key={c.period} value={c.period}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">ตั้งแต่</label>
            <input type="date" value={range.from} max={range.to} onChange={(e) => onDateInput('from', e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">ถึง</label>
            <input type="date" value={range.to} min={range.from} onChange={(e) => onDateInput('to', e.target.value)} className="input w-full" />
          </div>
        </div>
        {!selectedCycle && (
          <p className="text-xs text-gray-500 mt-2">
            ช่วงนี้ไม่ตรงรอบเงินเดือน ปุ่ม &ldquo;หักมาสายรายวัน&rdquo; จะออกทั้งงวด {dailyCycle.label} เพราะยอดหักคิดเป็นรายงวด
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> คนที่สาย</p>
          <p className="text-xl font-bold text-ink">{isLoading ? '-' : (data?.totals.employees_late ?? 0)}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">รวมครั้ง</p>
          <p className="text-xl font-bold text-ink">{isLoading ? '-' : (data?.totals.late_days ?? 0)}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> รวมเวลา</p>
          <p className="text-xl font-bold text-rose-700">{isLoading ? '-' : lateLabel(data?.totals.late_minutes ?? 0)}</p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
        แสดงคนไม่สายด้วย
      </label>

      {isError ? (
        <ErrorState detail="โหลดสรุปมาสายไม่สำเร็จ" onRetry={() => void refetch()} />
      ) : !isLoading && rows.length === 0 ? (
        <EmptyState icon={Clock} title="ไม่มีคนมาสายในช่วงนี้" />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-max">
            <thead>
              <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2 font-medium whitespace-nowrap">รหัส</th>
                <th className="px-2 py-2 font-medium whitespace-nowrap">ชื่อ</th>
                <th className="px-2 py-2 font-medium whitespace-nowrap">แผนก</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">จำนวนครั้ง</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">รวมเวลา</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">เฉลี่ยต่อครั้ง</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">สายมากสุด</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap" title="วันทำงานที่ไม่มีสแกนเข้างานและไม่มีใบลาที่อนุมัติ (วันนี้ยังไม่นับ)">ขาด (วัน)</th>
                {leaveColumns.map((c) => (
                  <th key={c.type} className="px-2 py-2 font-medium text-right whitespace-nowrap" title="ใบลาที่อนุมัติแล้ว นับเป็นวัน ลาครึ่งวัน = 0.5">{c.label}</th>
                ))}
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap" title="วันลาที่แจ้งกะทันหัน นับอยู่ในลาป่วยหรือลากิจแล้ว">ลาฉุกเฉิน</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows colSpan={9 + leaveColumns.length} />
              ) : (
                rows.map((r) => {
                  const avg = r.late_days > 0 ? r.late_minutes / r.late_days : 0;
                  return (
                    <tr
                      key={r.emp_code}
                      className="border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        navigate(`/hr/attendance?emp=${encodeURIComponent(r.emp_code)}&from=${range.from}&to=${range.to}`)
                      }
                    >
                      <td className="px-3 py-1.5 font-mono text-xs text-gray-500 whitespace-nowrap">{r.emp_code}</td>
                      <td className="px-2 py-1.5 text-gray-900 whitespace-nowrap">{r.name}</td>
                      <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{r.department ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{r.late_days}</td>
                      <td className="px-2 py-1.5 text-right font-semibold text-rose-700 whitespace-nowrap">{lateLabel(r.late_minutes)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-500 whitespace-nowrap">{r.late_days > 0 ? lateLabel(avg) : '—'}</td>
                      <td className="px-2 py-1.5 text-right text-gray-500 whitespace-nowrap">
                        {r.worst_date ? `${formatShortDate(r.worst_date)} (${lateLabel(r.worst_minutes)})` : '—'}
                      </td>
                      <td className={`px-2 py-1.5 text-right whitespace-nowrap ${r.absent_days ? 'font-semibold text-amber-700' : 'text-gray-400'}`}>
                        {dayCount(r.absent_days)}
                      </td>
                      {leaveColumns.map((c) => {
                        const days = r.leave_by_type == null ? null : (r.leave_by_type[c.type] ?? 0);
                        return (
                          <td key={c.type} className={`px-2 py-1.5 text-right whitespace-nowrap ${days ? 'text-gray-700' : 'text-gray-400'}`}>
                            {dayCount(days)}
                          </td>
                        );
                      })}
                      <td className={`px-2 py-1.5 text-right whitespace-nowrap ${r.emergency_days ? 'font-medium text-amber-700' : 'text-gray-400'}`}>
                        {dayCount(r.emergency_days)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
